import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Download, CheckCircle, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

export default function CalendarTutorial() {
  const calendars = [
    {
      id: 'google',
      name: 'Google Calendar',
      icon: '📅',
      color: 'bg-blue-500',
      steps: [
        { 
          title: 'Descarga el archivo .ics',
          desc: 'Desde Minut-IA, haz clic en tu evento y selecciona "Sincronizar con mi calendario". Elige Google Calendar y descarga el archivo .ics.'
        },
        { 
          title: 'Abre Google Calendar',
          desc: 'Ve a calendar.google.com en tu navegador.'
        },
        { 
          title: 'Busca la opción de importar',
          desc: 'En el menú lateral izquierdo, haz clic en el símbolo "+" junto a "Otros calendarios".'
        },
        { 
          title: 'Selecciona "Importar"',
          desc: 'En el menú desplegable, haz clic en "Importar".'
        },
        { 
          title: 'Selecciona el archivo',
          desc: 'Haz clic en "Seleccionar archivo de tu ordenador" y busca el archivo .ics que descargaste.'
        },
        { 
          title: 'Elige el calendario destino',
          desc: 'Selecciona en qué calendario de Google quieres importar el evento (normalmente tu calendario principal).'
        },
        { 
          title: 'Importar',
          desc: 'Haz clic en el botón "Importar".'
        },
        { 
          title: '¡Listo!',
          desc: 'Tu evento aparecerá en Google Calendar. Recibirás las notificaciones que hayas configurado en Google.'
        }
      ]
    },
    {
      id: 'outlook',
      name: 'Outlook / Microsoft 365',
      icon: '📆',
      color: 'bg-blue-600',
      steps: [
        { 
          title: 'Descarga el archivo .ics',
          desc: 'Desde Minut-IA, obtén el archivo .ics de tu evento usando "Sincronizar con mi calendario".'
        },
        { 
          title: 'Abre Outlook',
          desc: 'Accede a outlook.com o abre la aplicación de Outlook en tu ordenador.'
        },
        { 
          title: 'Ve a tu calendario',
          desc: 'Haz clic en el ícono de calendario en la parte inferior izquierda.'
        },
        { 
          title: 'Agregar calendario',
          desc: 'Haz clic en "Agregar calendario" en la barra superior.'
        },
        { 
          title: 'Cargar desde archivo',
          desc: 'Selecciona "Cargar desde archivo" en el menú.'
        },
        { 
          title: 'Examinar archivo',
          desc: 'Haz clic en "Examinar" y selecciona el archivo .ics descargado.'
        },
        { 
          title: 'Selecciona calendario destino',
          desc: 'Elige en qué calendario de Outlook quieres importar el evento.'
        },
        { 
          title: 'Importar',
          desc: 'Haz clic en "Importar" y tu evento aparecerá en Outlook Calendar.'
        }
      ]
    },
    {
      id: 'apple',
      name: 'Apple Calendar (macOS/iOS)',
      icon: '🍎',
      color: 'bg-gray-700',
      steps: [
        { 
          title: 'Descarga el archivo .ics',
          desc: 'Obtén el archivo .ics desde Minut-IA.'
        },
        { 
          title: 'En Mac: Abre Calendar',
          desc: 'Abre la aplicación "Calendario" en tu Mac.'
        },
        { 
          title: 'Importar desde menú',
          desc: 'Ve al menú "Archivo" → "Importar".'
        },
        { 
          title: 'Selecciona el archivo',
          desc: 'Busca y selecciona el archivo .ics que descargaste.'
        },
        { 
          title: 'Elige el calendario',
          desc: 'Selecciona en qué calendario de Apple deseas agregar el evento.'
        },
        { 
          title: 'Aceptar',
          desc: 'Haz clic en "Aceptar" para importar.'
        },
        { 
          title: 'En iPhone/iPad',
          desc: 'Si enviaste el archivo .ics por correo o lo descargaste en tu dispositivo iOS, simplemente toca el archivo y selecciona "Agregar al Calendario".'
        },
        { 
          title: 'Sincronización iCloud',
          desc: 'Si tienes iCloud activado, el evento se sincronizará automáticamente en todos tus dispositivos Apple.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/calendario">
            <Button variant="ghost" className="mb-4 gap-2">
              <ArrowLeft size={18} />
              Volver al Calendario
            </Button>
          </Link>
          
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3 mb-2">
            <Calendar className="text-orange-500" size={40} />
            Tutorial de Sincronización
          </h1>
          <p className="text-gray-600">
            Aprende a importar tus eventos de Minut-IA a tu calendario favorito
          </p>
        </div>

        {/* Introduction */}
        <Card className="mb-8 border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <CheckCircle className="text-orange-500" />
              ¿Cómo funciona?
            </h2>
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                <strong>1.</strong> Cuando creas un evento en Minut-IA, puedes exportarlo como archivo .ics (formato estándar de calendario).
              </p>
              <p>
                <strong>2.</strong> Este archivo .ics puede importarse en cualquier aplicación de calendario moderna.
              </p>
              <p>
                <strong>3.</strong> Una vez importado, el evento aparecerá en tu calendario con todos sus detalles: título, fecha, hora, ubicación y descripción.
              </p>
              <p className="pt-2 text-orange-700 font-medium">
                💡 <strong>Importante:</strong> Las notificaciones de recordatorio llegarán por correo desde Minut-IA. 
                Si quieres notificaciones adicionales de tu calendario, configúralas después de importar el evento.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tutorials for each calendar */}
        <div className="space-y-8">
          {calendars.map((calendar) => (
            <Card key={calendar.id}>
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <span className={`${calendar.color} text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl`}>
                    {calendar.icon}
                  </span>
                  <span className="text-2xl">{calendar.name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {calendar.steps.map((step, index) => (
                    <div key={index} className="flex gap-4">
                      <div className={`
                        flex-shrink-0 w-8 h-8 rounded-full ${calendar.color} text-white 
                        flex items-center justify-center font-bold text-sm
                      `}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">{step.title}</h4>
                        <p className="text-sm text-gray-600">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Visual tip */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-700">
                    <strong>💡 Consejo:</strong> Si tienes problemas, asegúrate de que el archivo se descargó completamente 
                    y tiene la extensión <code className="bg-gray-200 px-1 rounded">.ics</code>
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-2xl">Preguntas Frecuentes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                ¿Puedo sincronizar automáticamente todos mis eventos?
              </h4>
              <p className="text-sm text-gray-600">
                Actualmente cada evento debe exportarse individualmente. Estamos trabajando en una funcionalidad 
                de sincronización automática con Google Calendar, Outlook y Apple Calendar.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                ¿Se actualizan los cambios automáticamente?
              </h4>
              <p className="text-sm text-gray-600">
                No. Si modificas el evento en Minut-IA después de exportarlo, necesitarás exportarlo nuevamente 
                y reemplazar el evento en tu calendario.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                ¿Funcionará con otros calendarios?
              </h4>
              <p className="text-sm text-gray-600">
                Sí. El formato .ics es un estándar universal soportado por prácticamente todos los calendarios modernos: 
                Thunderbird, Yahoo Calendar, Zoho Calendar, etc.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                ¿Recibiré notificaciones duplicadas?
              </h4>
              <p className="text-sm text-gray-600">
                Recibirás el recordatorio de Minut-IA por correo 1 hora antes del evento. Si configuras 
                notificaciones adicionales en tu calendario, recibirás ambas. Puedes desactivar las que prefieras.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Call to action */}
        <div className="mt-8 text-center p-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl text-white">
          <h3 className="text-2xl font-bold mb-2">¿Listo para empezar?</h3>
          <p className="mb-4">Crea tu primer evento y sincronízalo con tu calendario favorito</p>
          <Link to="/calendario">
            <Button size="lg" variant="secondary" className="gap-2">
              <Calendar size={20} />
              Ir al Calendario
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
